import { Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'

import { TextInput } from '@salsita-ai/platform-project-sdk/ui'

const Form = styled('form')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  alignItems: 'center',
}))

const requiredMessage = `This field is required`

const invalidEmailMessage = `Email address is invalid`
const invalidPhoneNumberMessage = `Phone number is invalid`

const firstNamePlaceholder = `First name*`
const lastNamePlaceholder = `Last name*`
const emailPlaceholder = `Email address*`
const phoneNumberPlaceholder = `Phone number`
const messagePlaceholder = `Message*`

interface AnonymousFinalizeFormInputs {
  emailAddress: string
  firstName: string
  lastName: string
  phoneNumber: string
  message: string
}

interface FinalizeFormProps {
  onSubmit: (data: unknown) => void
  formId: string
}
export const FinalizeForm = ({ onSubmit: onSubmitCallback, formId }: FinalizeFormProps) => {
  const { handleSubmit, control } = useForm<AnonymousFinalizeFormInputs>()

  const onSubmit: SubmitHandler<AnonymousFinalizeFormInputs> = data => {
    onSubmitCallback(data)
  }

  return (
    <Form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2} direction="row">
        <Controller
          name="firstName"
          defaultValue=""
          control={control}
          rules={{
            required: requiredMessage,
          }}
          render={({ field, fieldState }) => (
            <TextInput
              type="text"
              placeholder={firstNamePlaceholder}
              hasError={!!fieldState.error}
              helperText={fieldState.error?.message ?? ' '}
              {...field}
            />
          )}
        />
        <Controller
          name="lastName"
          defaultValue=""
          control={control}
          rules={{
            required: requiredMessage,
          }}
          render={({ field, fieldState }) => (
            <TextInput
              type="text"
              placeholder={lastNamePlaceholder}
              hasError={!!fieldState.error}
              helperText={fieldState.error?.message ?? ' '}
              {...field}
            />
          )}
        />
      </Stack>
      <Controller
        name="emailAddress"
        defaultValue=""
        control={control}
        rules={{
          required: requiredMessage,
          pattern: {
            value: /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: invalidEmailMessage,
          },
        }}
        render={({ field, fieldState }) => (
          <TextInput
            type="email"
            placeholder={emailPlaceholder}
            hasError={!!fieldState.error}
            helperText={fieldState.error?.message ?? ' '}
            {...field}
          />
        )}
      />
      <Controller
        name="phoneNumber"
        defaultValue=""
        control={control}
        rules={{
          pattern: {
            value: /^[0-9]{10}$/,
            message: invalidPhoneNumberMessage,
          },
        }}
        render={({ field, fieldState }) => (
          <TextInput
            type="text"
            placeholder={phoneNumberPlaceholder}
            hasError={!!fieldState.error}
            helperText={fieldState.error?.message ?? ' '}
            {...field}
          />
        )}
      />
      <Controller
        name="message"
        defaultValue=""
        control={control}
        rules={{
          required: requiredMessage,
          validate: (v: string) => (typeof v === 'string' && v.trim().length > 0) || requiredMessage,
        }}
        render={({ field, fieldState }) => (
          <TextInput
            type="text"
            multiline={true}
            rows={4}
            placeholder={messagePlaceholder}
            hasError={!!fieldState.error}
            helperText={fieldState.error?.message ?? ' '}
            {...field}
          />
        )}
      />
    </Form>
  )
}
